'use client'

import { cva, VariantProps } from 'class-variance-authority'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'
import { ComponentProps, useMemo } from 'react'
import { DateLibOptions, DayPicker } from 'react-day-picker'

import { cn } from '@/shared/lib'

import { buttonBase, buttonVariants } from '../button'
import { iconButtonVariants } from '../button/icon-button'
import { Icon } from '../icon'

import 'react-day-picker/style.css'

const cellVariants = cva('', {
  variants: {
    size: {
      small: 'h-[34px] w-[34px]',
      large: 'h-[42px] w-[42px]',
    },
  },
})

const modifiersClassNames = {
  firstOfMonth: 'first-of-month',
  lastOfMonth: 'last-of-month',
}

export type Props = ComponentProps<typeof DayPicker> &
  VariantProps<typeof cellVariants>

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = false,
  size = 'small',
  disabled,
  modifiers: modifiersProp,
  modifiersClassNames: modifiersClassNamesProp,
  ...props
}: Props) => {
  const isLargeCalendar = size === 'large'

  const isRangeCalendar = props.mode === 'range'
  const isMultipleCalendar = props.mode === 'multiple'
  const isSingleCalendar = props.mode === 'single'

  const isLargeRangeCalendar = isLargeCalendar && !isRangeCalendar

  const modifiers = useMemo(() => {
    return {
      firstOfMonth: (date: Date) => date.getDate() === 1,
      lastOfMonth: (date: Date) => {
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)

        return date.getDate() === lastDay.getDate()
      },
      ...modifiersProp,
    }
  }, [modifiersProp])

  const defaultMonth = (() => {
    if (!('selected' in props) || !props.selected) {
      return undefined
    }

    const { selected } = props

    // DateRange 타입인 경우 (range mode)
    if ('from' in selected && selected.from) {
      return selected.from
    }

    // Date 타입인 경우 (single mode)
    if (selected instanceof Date) {
      return selected
    }

    // Date[] 타입인 경우 (multiple mode)
    if (Array.isArray(selected) && selected.length > 0) {
      return selected[0]
    }
  })()

  const captionFormatter = (date: Date, options?: DateLibOptions) => {
    if (options?.locale?.code === 'ko') {
      return format(date, 'yyyy년 M월', { locale: options?.locale })
    }

    return format(date, 'MMM yyyy', { locale: options?.locale })
  }

  const weekdayFormatter = (weekday: Date, options?: DateLibOptions) => {
    return format(weekday, 'EEEEE', { locale: options?.locale })
  }

  const { day, ...restClassNames } = classNames || {}

  return (
    <DayPicker
      locale={ko}
      formatters={{
        formatCaption: captionFormatter,
        formatWeekdayName: weekdayFormatter,
      }}
      showOutsideDays={showOutsideDays}
      defaultMonth={defaultMonth}
      className={cn(
        'rounded-lg border-none bg-white p-6 shadow-[0px_0px_7px_0px_rgba(0,0,0,0.25)]',
        className,
      )}
      classNames={{
        months: 'flex flex-col sm:flex-row relative',
        month: cn(
          'flex flex-col gap-4',
          isLargeRangeCalendar && 'w-full gap-8',
          'sm:[&:not(:last-child)]:mr-6',
        ),
        month_caption: cn(
          'flex w-full items-center justify-center py-[3px]',
          isLargeRangeCalendar && 'justify-start py-0',
        ),
        caption_label: cn(
          'font-bold text-black',
          isLargeRangeCalendar ? 'typo-title2' : 'typo-headline3',
        ),
        nav: '',
        button_previous: cn(
          buttonVariants({
            variant: 'outline',
            color: 'primary',
            shape: 'pill',
          }),
          iconButtonVariants({ size: 'small' }),
          'absolute left-0',
          isLargeRangeCalendar && 'top-1 right-10 left-auto',
          '[&[aria-disabled]]:cursor-default [&[aria-disabled]]:text-gray-200 [&[aria-disabled]]:hover:bg-white [&[aria-disabled]_svg]:text-gray-200',
        ),
        button_next: cn(
          buttonVariants({
            variant: 'outline',
            color: 'primary',
            shape: 'pill',
          }),
          iconButtonVariants({ size: 'small' }),
          'absolute right-0',
          isLargeRangeCalendar && 'top-1',
          '[&[aria-disabled]]:cursor-default [&[aria-disabled]]:text-gray-200 [&[aria-disabled]]:hover:bg-white [&[aria-disabled]_svg]:text-gray-200',
        ),
        table: 'w-full border-collapse',
        weekdays: cn(
          'flex',
          size === 'large' ? 'mb-3 justify-between' : 'mb-2',
        ),
        weekday: cn(
          cellVariants({ size }),
          isLargeCalendar ? 'typo-body1' : 'typo-label1',
          'h-fit rounded-md font-medium text-gray-400',
          '[&[aria-label="Sunday"]]:text-red-500 [&[aria-label="일요일"]]:text-red-500',
        ),
        weeks: cn('space-y-1', size === 'large' && 'space-y-[20px]'),
        week: cn(
          'grid w-full grid-cols-7',
          isLargeCalendar && !isRangeCalendar && 'gap-x-[9px]',
        ),
        day: cn(
          cellVariants({ size }),
          isLargeCalendar ? 'typo-body1' : 'typo-label1',
          'relative p-0 text-center font-medium focus-within:relative focus-within:z-20',
          '[&[data-selected]]:bg-purple-50 [&[data-selected].day-range-end]:rounded-r-full [&[data-selected].day-range-start]:rounded-l-full',
          isRangeCalendar && [
            '[&.day-range-end]:rounded-r-md [&.day-range-start]:rounded-l-md',
          ],
          (isSingleCalendar || isMultipleCalendar) &&
            '[&[data-selected]]:rounded-full',
          day,
        ),
        day_button: cn(
          ...buttonBase,
          cellVariants({ size }),
          'rounded-full p-0 aria-selected:opacity-100',
          'hover:bg-purple-50',
        ),
        selected:
          'day-selected *:bg-black *:text-white *:hover:bg-black *:hover:text-white *:focus:bg-black *:focus:text-white',
        range_start: 'day-range-start *:bg-black *:text-white font-bold',
        range_middle: '*:!bg-purple-50 *:!text-black',

        range_end: 'day-range-end *:bg-black *:text-white font-bold',
        today: cn(
          'after:absolute after:bottom-[3px] after:left-1/2 after:h-[3px] after:w-[3px] after:-translate-x-1/2 after:rounded-full after:bg-black after:content-[""]',
          '[&[data-disabled]:after]:bg-gray-400',
          isLargeCalendar && 'after:bottom-[5px] after:h-[4px] after:w-[4px]',
          isRangeCalendar
            ? '[&[data-selected].day-range-end]:after:bg-white [&[data-selected].day-range-start]:after:bg-white'
            : '[&[data-selected]]:after:bg-white',
        ),
        outside: 'day-outside text-gray-400 aria-selected:text-gray-400',
        disabled: 'text-gray-400',
        hidden: 'invisible',
        ...restClassNames,
      }}
      components={{
        Chevron: ({ orientation, className, ...props }) => (
          <Icon
            name={orientation === 'left' ? 'arrow-left' : 'arrow-right'}
            className={cn('size-4', className)}
            {...props}
          />
        ),
      }}
      disabled={disabled}
      modifiers={modifiers}
      {...props}
      modifiersClassNames={{
        ...modifiersClassNames,
        ...modifiersClassNamesProp,
      }}
      animate={true}
    />
  )
}
